import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/Login';
import { AcceptInvitationPage } from '@/pages/AcceptInvitation';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { VerifyEmailPage } from '@/pages/VerifyEmail';
import { DashboardPage } from '@/pages/Dashboard';
import { DocumentsPage } from '@/pages/Documents';
import { SearchPage } from '@/pages/Search';
import { ProjectsPage } from '@/pages/Projects';
import { ProjectDetailPage } from '@/pages/ProjectDetail';
import { MyQueuePage } from '@/pages/MyQueue';
import { HelpPage } from '@/pages/Help';
import { ChangelogPage } from '@/pages/Changelog';
import { DocumentDetailPage } from '@/pages/DocumentDetail';
import { WorkflowsPage } from '@/pages/Workflows';
import { WorkflowRunPage } from '@/pages/WorkflowRun';
import { WorkflowDesignerPage } from '@/pages/WorkflowDesigner';
import { ExtractionReviewPage } from '@/pages/ExtractionReview';
import { AuditLogPage } from '@/pages/AuditLog';
import { AdminPage } from '@/pages/Admin';
import { GdprDsrPage } from '@/pages/admin/GdprDsrPage';
import { AIGovernancePage } from '@/pages/admin/AIGovernancePage';
import { CompliancePage } from '@/pages/Compliance';
import { PVCasesPage } from '@/pages/PVCases';
import { PVCaseDetailPage } from '@/pages/PVCaseDetail';
import { PVReportingPage } from '@/pages/PVReporting';
import { ComplianceScansPage } from '@/pages/ComplianceScans';
import { ComplianceScanDetailPage } from '@/pages/ComplianceScanDetail';
import { PsmfListPage } from '@/pages/PsmfList';
import { PsmfBuilderPage } from '@/pages/PsmfBuilder';
import { SignalsPage } from '@/pages/Signals';
import { SignalDetailPage } from '@/pages/SignalDetail';
import { PracActionsPage } from '@/pages/PracActions';
import { PracActionDetailPage } from '@/pages/PracActionDetail';
import { RmpListPage } from '@/pages/RmpList';
import { RmpDetailPage } from '@/pages/RmpDetail';
import { PsurCalendarPage } from '@/pages/PsurCalendar';
import { PsurListPage } from '@/pages/PsurList';
import { PsurDetailPage } from '@/pages/PsurDetail';
import { LiteraturePage } from '@/pages/Literature';
import { DuplicatesQueuePage } from '@/pages/DuplicatesQueue';
import { RegIntelAlertsPage } from '@/pages/RegIntelAlerts';
import { EurdListPage } from '@/pages/EurdList';
import { EurdEntryDetailPage } from '@/pages/EurdEntryDetail';
import { CompliancePackPage } from '@/pages/CompliancePack';
import { PackShareLinksPage } from '@/pages/PackShareLinks';
import { AdminModelRegistryPage } from '@/pages/admin/AdminModelRegistry';
import { AdminDriftPage } from '@/pages/admin/AdminDrift';
import { AdminPromptVersionsPage } from '@/pages/admin/AdminPromptVersions';
import { AdminInvoicesPage } from '@/pages/admin/AdminInvoices';
import { AdminDiagnosticsPage } from '@/pages/admin/AdminDiagnostics';
import { SettingsLayout } from '@/pages/SettingsLayout';
import { SettingsProfilePage } from '@/pages/SettingsProfile';
import { SettingsSecurityPage } from '@/pages/SettingsSecurity';
import { SettingsTeamPage } from '@/pages/SettingsTeam';
import { SettingsTenantPage } from '@/pages/SettingsTenant';
import { SettingsWebhooksPage } from '@/pages/SettingsWebhooks';
import { SettingsApiKeysPage } from '@/pages/SettingsApiKeys';
import { WebhookDocsPage } from '@/pages/WebhookDocs';
import { SettingsPackTemplatePage } from '@/pages/SettingsPackTemplate';
import { SettingsUsagePage } from '@/pages/SettingsUsage';
import { SettingsBillingPage } from '@/pages/SettingsBilling';
import { SettingsSsoPage } from '@/pages/SettingsSso';
import { PlatformAdminLayout } from '@/pages/platform/PlatformAdminLayout';
import { PlatformOverviewPage } from '@/pages/platform/PlatformOverview';
import { PlatformTenantsPage } from '@/pages/platform/PlatformTenants';
import { PlatformTenantDetailPage } from '@/pages/platform/PlatformTenantDetail';
import { PlatformUsersPage } from '@/pages/platform/PlatformUsers';
import { PlatformSubscriptionsPage } from '@/pages/platform/PlatformSubscriptions';
import { PlatformAuditPage } from '@/pages/platform/PlatformAudit';
import { PlatformLeadsPage } from '@/pages/platform/PlatformLeads';
import { PlatformCostPage } from '@/pages/platform/PlatformCost';
import { PlatformSnapshotIntakesPage } from '@/pages/platform/PlatformSnapshotIntakes';
import { OnboardingPage } from '@/pages/Onboarding';
import { TermsPage } from '@/pages/legal/TermsPage';
import { PrivacyPage } from '@/pages/legal/PrivacyPage';
import { CookiesPage } from '@/pages/legal/CookiesPage';
import { DPAPage } from '@/pages/legal/DPAPage';
import { SubprocessorsPage } from '@/pages/legal/SubprocessorsPage';
import { Toaster } from '@/components/ui/toaster';
import { useThemeStore } from '@/stores/theme';
import { applyTenantBranding } from '@/lib/branding';

export function App() {
  const isAuthenticated = useAuthStore((s) => !!s.accessToken && !!s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const theme = useThemeStore((s) => s.theme);

  // Apply dark-class on <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Apply per-tenant brand colour
  useEffect(() => {
    applyTenantBranding(tenant?.brandingColor ?? null);
  }, [tenant?.brandingColor]);

  return (
    <>
      <Routes>
        {/* Legal (always public) */}
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/cookies" element={<CookiesPage />} />
        <Route path="/legal/dpa" element={<DPAPage />} />
        <Route path="/legal/subprocessors" element={<SubprocessorsPage />} />

        {/* Public auth routes (always available) */}
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
        {isAuthenticated && (
          <Route path="/platform-admin" element={<PlatformAdminLayout />}>
            <Route index element={<PlatformOverviewPage />} />
            <Route path="tenants" element={<PlatformTenantsPage />} />
            <Route path="tenants/:id" element={<PlatformTenantDetailPage />} />
            <Route path="users" element={<PlatformUsersPage />} />
            <Route path="subscriptions" element={<PlatformSubscriptionsPage />} />
            <Route path="leads" element={<PlatformLeadsPage />} />
            <Route path="cost" element={<PlatformCostPage />} />
            <Route path="snapshot-intakes" element={<PlatformSnapshotIntakesPage />} />
            <Route path="audit" element={<PlatformAuditPage />} />
          </Route>
        )}
        {isAuthenticated && (
          <Route path="/onboarding" element={<OnboardingPage />} />
        )}
        {isAuthenticated ? (
          <Route element={<AppShell />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/my-queue" element={<MyQueuePage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/docs/webhooks" element={<WebhookDocsPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/documents/:id" element={<DocumentDetailPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/workflows" element={<WorkflowsPage />} />
            <Route path="/workflows/new" element={<WorkflowDesignerPage />} />
            <Route path="/workflows/runs/:id" element={<WorkflowRunPage />} />
            <Route path="/extractions/:id" element={<ExtractionReviewPage />} />
            <Route path="/pv" element={<PVCasesPage />} />
            <Route path="/pv/reporting" element={<PVReportingPage />} />
            <Route path="/pv/:id" element={<PVCaseDetailPage />} />
            <Route path="/pv/duplicates" element={<DuplicatesQueuePage />} />
            <Route path="/signals" element={<SignalsPage />} />
            <Route path="/signals/:id" element={<SignalDetailPage />} />
            <Route path="/prac" element={<PracActionsPage />} />
            <Route path="/prac/:id" element={<PracActionDetailPage />} />
            <Route path="/rmp" element={<RmpListPage />} />
            <Route path="/rmp/:id" element={<RmpDetailPage />} />
            <Route path="/psur" element={<PsurListPage />} />
            <Route path="/psur/:id" element={<PsurDetailPage />} />
            <Route path="/psur-calendar" element={<PsurCalendarPage />} />
            <Route path="/literature" element={<LiteraturePage />} />
            <Route path="/regulatory-intel" element={<RegIntelAlertsPage />} />
            <Route path="/eurd" element={<EurdListPage />} />
            <Route path="/eurd/:id" element={<EurdEntryDetailPage />} />
            <Route path="/compliance" element={<ComplianceScansPage />} />
            <Route path="/compliance/:id" element={<ComplianceScanDetailPage />} />
            <Route path="/psmf" element={<PsmfListPage />} />
            <Route path="/psmf/new" element={<PsmfBuilderPage />} />
            <Route path="/psmf/:id" element={<PsmfBuilderPage />} />
            <Route path="/compliance-register" element={<CompliancePage />} />
            <Route path="/compliance-pack" element={<CompliancePackPage />} />
            <Route path="/compliance-pack/shares" element={<PackShareLinksPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/gdpr" element={<GdprDsrPage />} />
            <Route path="/admin/ai-governance" element={<AIGovernancePage />} />
            <Route path="/admin/model-registry" element={<AdminModelRegistryPage />} />
            <Route path="/admin/drift" element={<AdminDriftPage />} />
            <Route path="/admin/prompt-versions" element={<AdminPromptVersionsPage />} />
            <Route path="/admin/invoices" element={<AdminInvoicesPage />} />
            <Route path="/admin/diagnostics" element={<AdminDiagnosticsPage />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<SettingsProfilePage />} />
              <Route path="security" element={<SettingsSecurityPage />} />
              <Route path="team" element={<SettingsTeamPage />} />
              <Route path="tenant" element={<SettingsTenantPage />} />
              <Route path="pack-template" element={<SettingsPackTemplatePage />} />
              <Route path="usage" element={<SettingsUsagePage />} />
              <Route path="billing" element={<SettingsBillingPage />} />
              <Route path="sso" element={<SettingsSsoPage />} />
              <Route path="webhooks" element={<SettingsWebhooksPage />} />
              <Route path="api-keys" element={<SettingsApiKeysPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
      <Toaster />
    </>
  );
}
